import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import {
  ArrowRight,
  ArrowUpRight,
  Building2,
  Check,
  FileCode2,
  GitBranch,
  KeyRound,
  Landmark,
  Package,
  Scale,
  ShieldAlert,
  Wrench,
} from "lucide-react";
import { Link } from "react-router-dom";
import MarketingLayout from "../components/marketing/MarketingLayout";
import {
  EASE,
  Em,
  Eyebrow,
  GhostButton,
  GradientButton,
  Headline,
  Panel,
  Reveal,
  Section,
  SERIF,
  Subhead,
} from "../components/marketing/primitives";
import {
  CONTROL_COUNT,
  CREDENTIALS,
  DOMAINS,
  FLOW,
  LEAD_DIFFERENTIATORS,
  WHY_NOW,
} from "../components/marketing/content";
import { usePageMeta } from "../components/marketing/usePageMeta";

/** Mount-time stagger for the hero. Plays immediately, not on scroll. */
function Enter({
  children,
  delay = 0,
  y = 16,
  className = "",
}: {
  children: React.ReactNode;
  delay?: number;
  y?: number;
  className?: string;
}) {
  const reduce = useReducedMotion();
  if (reduce) return <div className={className}>{children}</div>;
  return (
    <motion.div
      initial={{ opacity: 0, y }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay, ease: EASE }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/**
 * Landing page. Warm editorial, plain-spoken, concrete.
 *
 * Positioning (Oct 2026 uplift, from the Nucleus Systems differentiation
 * brief): unify security, protect the supply chain, prove code trust.
 * Paxley is the evidence layer for the NS-CTAF framework, not one more
 * scanner.
 *
 * Structure and voice rules:
 *   - The hero visual is real product output (Paxley's own scan of a public
 *     reference application), never an abstraction.
 *   - Every market figure links to its source; every product claim is one
 *     the platform backs today (see the rules in marketing/content.ts).
 *   - Three buyers are named (engineering teams; investors and acquirers;
 *     regulated software makers) and each gets a path in.
 *   - Each section uses a different layout family.
 *   - One CTA label per intent across the whole site: "Start free scan",
 *     "Book an assessment", "Request a guided trial".
 *   - Section eyebrows are rationed (four on this page).
 */
export default function Landing() {
  usePageMeta(
    "Paxley | Code security that proves trust",
    "One platform for code, dependencies, secrets, infrastructure and SBOMs, with Code Trust evidence for customers, boards, regulators and acquirers."
  );
  return (
    <MarketingLayout>
      <HeroSection />
      <MarqueeRibbon />
      <WhyNowSection />
      <DifferenceSection />
      <WhatItFindsSection />
      <DifferentiatorsSection />
      <CodeTrustSection />
      <AudienceSection />
      <HowItWorksSection />
      <CredibilitySection />
      <PricingStripSection />
      <FinalCTASection />
    </MarketingLayout>
  );
}


// ══════════════════════════════════════════════════════════════════
// 1. HERO
// ══════════════════════════════════════════════════════════════════

function HeroSection() {
  return (
    <Section padding="hero" container="narrow">
      <div className="text-center">
        <Enter delay={0}>
          <Headline size="hero" className="mx-auto max-w-3xl">
            Code security that proves trust, <Em>not just finds bugs.</Em>
          </Headline>
        </Enter>
        <Enter delay={0.1}>
          <Subhead size="large" className="mx-auto mt-6 max-w-xl">
            One platform for code, dependencies, secrets, infrastructure and SBOMs, with evidence
            you can hand to customers, boards and acquirers.
          </Subhead>
        </Enter>
        <Enter delay={0.2}>
          <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <GradientButton to="/onboarding" size="lg">
              Start free scan
            </GradientButton>
            <GhostButton to="/inquiry?interest=assessment" size="lg">
              Book an assessment
            </GhostButton>
          </div>
        </Enter>
      </div>

      <Enter delay={0.34} y={28} className="mx-auto mt-14 max-w-2xl">
        <ReportPreview />
        <p className="mt-3 text-center text-xs opacity-50">
          Real output: Paxley&rsquo;s scan of Microsoft&rsquo;s public eShopOnWeb reference
          application. Every number is checkable.
        </p>
      </Enter>
    </Section>
  );
}

/**
 * Slow ribbon of the things a scan actually catches. The one continuous
 * motion on the page, and it earns it: the list is the product's scope.
 * Duplicated content = seamless CSS loop; pauses on hover; static under
 * prefers-reduced-motion (see .marquee-track in index.css).
 */
function MarqueeRibbon() {
  const items = [
    "SQL injection",
    "leaked credentials",
    "vulnerable transitive packages",
    "cross-site scripting",
    "containers running as root",
    "GPL inside proprietary code",
    "out-of-support runtimes",
    "hardcoded connection strings",
    "open redirects",
    "missing SBOM entries",
    "abandoned migrations",
    "known-exploited CVEs",
  ];
  const row = items.map((t, i) => (
    <span key={i} className="mx-4 inline-flex items-center gap-4 whitespace-nowrap">
      <span className="font-mono text-sm opacity-70">{t}</span>
      <span className="h-3 w-px bg-[#17150F]/20" aria-hidden="true" />
    </span>
  ));
  return (
    <div className="mask-fade-x overflow-hidden border-y border-[#E4DFD3] py-4" aria-hidden="true">
      <div className="marquee-track">
        <div className="flex items-center">{row}</div>
        <div className="flex items-center">{row}</div>
      </div>
    </div>
  );
}

/**
 * A real component preview of Paxley's report surface, populated with the
 * measured results of the eShopOnWeb scan. Not a mock: every figure below
 * is reproducible by scanning the same public repository.
 */
function ReportPreview() {
  return (
    <div className="overflow-hidden rounded-2xl border border-[#17150F]/15 bg-white shadow-[0_16px_48px_-20px_rgba(23,21,15,0.25)]">
      <div className="flex items-center justify-between border-b border-[#E4DFD3] px-5 py-3.5">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold">eShopOnWeb</p>
          <p className="text-[11px] opacity-50">ASP.NET reference application</p>
        </div>
        <span className="shrink-0 rounded-md border border-[#17150F]/15 px-2 py-1 text-[10px] font-semibold uppercase tracking-wider opacity-70">
          Report
        </span>
      </div>

      <div className="grid grid-cols-4 divide-x divide-[#E4DFD3] border-b border-[#E4DFD3] text-center">
        {[
          { n: "13", label: "critical", tone: "text-red-700" },
          { n: "12", label: "high", tone: "text-orange-600" },
          { n: "33", label: "medium", tone: "text-amber-600" },
          { n: "3", label: "low", tone: "text-stone-500" },
        ].map((s) => (
          <div key={s.label} className="px-2 py-3.5">
            <p className={`text-2xl font-semibold tabular-nums ${s.tone}`} style={SERIF}>
              {s.n}
            </p>
            <p className="text-[10px] uppercase tracking-wider opacity-50">{s.label}</p>
          </div>
        ))}
      </div>

      <ul className="text-left text-sm">
        <li className="flex items-start gap-3 px-5 py-3.5">
          <KeyRound className="mt-0.5 h-4 w-4 shrink-0 text-red-700" />
          <div className="min-w-0">
            <p className="font-medium">Database password committed in a config file</p>
            <p className="mt-0.5 truncate font-mono text-xs opacity-50">
              src/Web/appsettings.json
            </p>
          </div>
        </li>
        <li className="flex items-start gap-3 border-t border-[#E4DFD3] px-5 py-3.5">
          <Package className="mt-0.5 h-4 w-4 shrink-0 text-orange-600" />
          <div className="min-w-0">
            <p className="font-medium">
              9 vulnerable packages, 8 of them pulled in by other dependencies
            </p>
            <p className="mt-0.5 truncate font-mono text-xs opacity-50">
              301 packages resolved, fix version known for all 9
            </p>
          </div>
        </li>
        <li className="flex items-start gap-3 border-t border-[#E4DFD3] px-5 py-3.5">
          <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
          <div className="min-w-0">
            <p className="font-medium">User input rendered into a page without encoding</p>
            <p className="mt-0.5 truncate font-mono text-xs opacity-50">
              src/Web/Areas/Identity/Pages/Account/Register.cshtml
            </p>
          </div>
        </li>
      </ul>

      <div className="flex flex-wrap gap-2 border-t border-[#E4DFD3] bg-[#FBF8F1] px-5 py-3.5">
        {["SBOM: 307 components", "Licenses reviewed", "Runtime supported until Nov 2026"].map(
          (chip) => (
            <span
              key={chip}
              className="rounded-md border border-[#E4DFD3] bg-white px-2 py-1 text-[11px] opacity-80"
            >
              {chip}
            </span>
          )
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// WHY NOW: 5/7 split, sourced figures as stacked rows
// ══════════════════════════════════════════════════════════════════

function WhyNowSection() {
  return (
    <Section>
      <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
        <Reveal className="lg:col-span-5">
          <Eyebrow>Why now</Eyebrow>
          <Headline size="section" className="mt-4">
            The software supply chain is <Em>the way in.</Em>
          </Headline>
          <Subhead className="mt-4">
            Buyers, regulators and acquirers now expect continuous vulnerability management, a
            known inventory of what you ship, and evidence when they ask for it.
          </Subhead>
        </Reveal>

        <div className="lg:col-span-7">
          {WHY_NOW.map((f, i) => (
            <Reveal key={f.figure} delay={0.06 * i}>
              <div className="grid gap-4 border-t border-[#E4DFD3] py-7 sm:grid-cols-[11rem_1fr] sm:gap-8">
                <p className="whitespace-nowrap text-5xl leading-none tracking-tight text-[#1E4D38]" style={SERIF}>
                  {f.figure}
                </p>
                <div>
                  <h3 className="text-base font-semibold">{f.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed opacity-75">{f.body}</p>
                  <a
                    href={f.source.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-3 inline-flex items-center gap-1 text-xs opacity-55 underline-offset-2 transition-opacity hover:underline hover:opacity-90"
                  >
                    {f.source.label}
                    <ArrowUpRight className="h-3 w-3" />
                  </a>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </Section>
  );
}

// ══════════════════════════════════════════════════════════════════
// THE DIFFERENCE: white panel, old approach vs Paxley, then the flow
// ══════════════════════════════════════════════════════════════════

function DifferenceSection() {
  const pillars = ["Unify security", "Protect the supply chain", "Prove code trust"];
  return (
    <Section container="none" padding="tight">
      <Panel tone="white">
        <div className="mx-auto max-w-6xl px-6 py-16 sm:px-10 md:py-20">
          <Reveal className="max-w-3xl">
            <Headline size="section">
              Not another scanner. <Em>An evidence layer.</Em>
            </Headline>
          </Reveal>

          <div className="mt-10 grid gap-6 md:grid-cols-[1fr_2fr]">
            <Reveal>
              <div className="h-full rounded-2xl bg-[#FBF8F1] p-6">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] opacity-50">
                  The usual approach
                </p>
                <p className="mt-3 text-lg leading-snug">
                  Find vulnerabilities across separate tools, then reconcile five dashboards by hand.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.06}>
              <div className="h-full rounded-2xl bg-[#1E4D38] p-6 text-[#F5F1E6]">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] opacity-60">
                  Paxley
                </p>
                <ol className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
                  {pillars.map((p, i) => (
                    <li key={p} className="flex items-center gap-4">
                      <span className="text-2xl leading-tight" style={SERIF}>
                        {p}
                      </span>
                      {i < pillars.length - 1 && (
                        <ArrowRight className="hidden h-4 w-4 shrink-0 opacity-50 sm:block" aria-hidden="true" />
                      )}
                    </li>
                  ))}
                </ol>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.1}>
            <ol className="mt-10 grid gap-px overflow-hidden rounded-2xl border border-[#E4DFD3] bg-[#E4DFD3] sm:grid-cols-5">
              {FLOW.map((step, i) => (
                <li key={step} className="bg-white px-5 py-5">
                  <span className="font-mono text-xs opacity-45">0{i + 1}</span>
                  <p className="mt-2 text-sm font-semibold leading-snug">{step}</p>
                </li>
              ))}
            </ol>
          </Reveal>
        </div>
      </Panel>
    </Section>
  );
}

// ══════════════════════════════════════════════════════════════════
// WHY PAXLEY: 4/8 split, six numbered reasons on hairlines
// ══════════════════════════════════════════════════════════════════

function DifferentiatorsSection() {
  return (
    <Section>
      <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
        <Reveal className="lg:col-span-4">
          <div className="lg:sticky lg:top-28">
            <Headline size="section">
              Why teams choose <Em>Paxley.</Em>
            </Headline>
            <Subhead className="mt-4">
              Built for today&rsquo;s software security problems, not yesterday&rsquo;s tool
              landscape.
            </Subhead>
            <Link
              to="/why-paxley"
              className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-[#1E4D38] underline-offset-4 hover:underline"
            >
              All twelve reasons and how we compare
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </Reveal>

        <div className="grid gap-x-10 sm:grid-cols-2 lg:col-span-8">
          {LEAD_DIFFERENTIATORS.map((d, i) => (
            <Reveal key={d.n} delay={0.04 * i}>
              <div className="border-t border-[#E4DFD3] py-6">
                <span className="text-2xl opacity-35" style={SERIF}>
                  0{i + 1}
                </span>
                <h3 className="mt-2 text-base font-semibold">{d.title}</h3>
                <p className="mt-2 text-sm leading-relaxed opacity-75">{d.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </Section>
  );
}

// ══════════════════════════════════════════════════════════════════
// CODE TRUST: coal panel, the NS-CTAF domains as a weighted bar
// ══════════════════════════════════════════════════════════════════

const COVERAGE_TONE = {
  strong: "bg-[#9FD3A8]",
  partial: "bg-[#9FD3A8]/45",
  assessed: "bg-white/15",
} as const;

function CodeTrustSection() {
  return (
    <Section id="code-trust" container="none" padding="tight">
      <Panel tone="coal">
        <div className="mx-auto max-w-6xl px-6 py-16 sm:px-10 md:py-20">
          <div className="grid gap-10 lg:grid-cols-12 lg:gap-16">
            <Reveal className="lg:col-span-5">
              <Eyebrow className="!opacity-60">Code Trust</Eyebrow>
              <Headline size="section" className="mt-4">
                Measure code trust. <Em>Prove it.</Em>
              </Headline>
              <Subhead className="mt-4 !opacity-75">
                Paxley is the evidence layer for the Nucleus Systems Code Trust Assurance Framework
                (NS-CTAF): six domains, {CONTROL_COUNT} controls, five maturity levels and a Trust
                Score from 0 to 100, aligned with SLSA, NIST SSDF, OWASP SAMM, the EU CRA and EO
                14028.
              </Subhead>
              <div className="mt-8">
                <GhostButton
                  to="/code-trust"
                  icon={<ArrowRight className="h-4 w-4" />}
                  className="!border-[#F5F1E6]/40 !bg-transparent !text-[#F5F1E6] hover:!bg-white/10"
                >
                  Explore Code Trust
                </GhostButton>
              </div>
            </Reveal>

            <Reveal delay={0.08} className="lg:col-span-7">
              <div className="flex h-3 overflow-hidden rounded-full" aria-hidden="true">
                {DOMAINS.map((d) => (
                  <span
                    key={d.id}
                    className={`${COVERAGE_TONE[d.coverage]} border-r-2 border-[#141412] last:border-r-0`}
                    style={{ width: `${d.weight}%` }}
                  />
                ))}
              </div>
              <ul className="mt-6">
                {DOMAINS.map((d) => (
                  <li
                    key={d.id}
                    className="grid grid-cols-[2.5rem_1fr_auto] items-baseline gap-3 border-t border-white/15 py-3.5 text-sm"
                  >
                    <span className="font-mono text-xs opacity-50">{d.id}</span>
                    <span>
                      <span className="font-medium">{d.name}</span>
                      <span className="ml-2 text-xs opacity-50">{d.controls.length} controls</span>
                    </span>
                    <span className="flex items-center gap-2 text-xs opacity-70">
                      <span className={`h-2 w-2 rounded-full ${COVERAGE_TONE[d.coverage]}`} />
                      <span className="hidden sm:inline">
                        {d.coverage === "strong" ? "Platform evidence" : d.coverage === "partial" ? "Partial" : "Assessed"}
                      </span>
                      <span className="w-9 text-right tabular-nums">{d.weight}%</span>
                    </span>
                  </li>
                ))}
              </ul>
              <p className="mt-4 text-xs leading-relaxed opacity-50">
                Bar width is each domain&rsquo;s weight in the Trust Score. Domains the platform
                does not yet evidence are reviewed by Nucleus Systems in a Code Trust assessment.
              </p>
            </Reveal>
          </div>
        </div>
      </Panel>
    </Section>
  );
}

// ══════════════════════════════════════════════════════════════════
// CREDIBILITY: paragraph left, measured facts right
// ══════════════════════════════════════════════════════════════════

function CredibilitySection() {
  return (
    <Section>
      <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
        <Reveal className="lg:col-span-7">
          <Headline size="section">
            Built by people who do <Em>the diligence.</Em>
          </Headline>
          <p className="mt-6 text-base leading-relaxed opacity-80 md:text-lg">
            Paxley comes from Nucleus Systems, a cybersecurity advisory firm that has assessed
            code in more than 350 M&amp;A transactions. The team has used IBM AppScan, Veracode,
            Mend, Black Duck, Snyk, Checkmarx, SonarQube and more than 20 open-source tools in
            client work, and built Paxley around the gaps they kept finding.
          </p>
          <p className="mt-4 text-base leading-relaxed opacity-80 md:text-lg">
            It is led by Godfrey Kutumela, formerly of the IBM application security product
            development team, and was refined for two years as a managed service before launch.
          </p>
        </Reveal>
        <Reveal delay={0.08} className="lg:col-span-5">
          <dl className="grid gap-6 sm:grid-cols-2">
            {CREDENTIALS.map((c) => (
              <div key={c.k} className="border-t border-[#17150F]/20 pt-4">
                <dt className="text-3xl" style={SERIF}>
                  {c.k}
                </dt>
                <dd className="mt-1 text-sm leading-relaxed opacity-65">{c.v}</dd>
              </div>
            ))}
          </dl>
        </Reveal>
      </div>
    </Section>
  );
}

// ══════════════════════════════════════════════════════════════════
// 2. WHAT IT FINDS: the green panel, as a bento with two data cells
// ══════════════════════════════════════════════════════════════════

type CheckItem = {
  icon: React.ReactNode;
  title: string;
  body: string;
  example: string;
  /** Wide cells carry a measured figure from the eShopOnWeb scan. */
  figure?: { value: string; caption: string };
};

function WhatItFindsSection() {
  const checks: CheckItem[] = [
    {
      icon: <Package className="h-4 w-4" />,
      title: "Risky dependencies",
      body: "Known vulnerabilities in open-source packages, including the ones your packages pull in that you never see.",
      example: "a critical advisory in a package three levels deep",
      figure: { value: "301", caption: "packages resolved in eShopOnWeb. 9 vulnerable, 8 of them transitive." },
    },
    {
      icon: <ShieldAlert className="h-4 w-4" />,
      title: "Vulnerable code",
      body: "Injection, broken auth, unsafe deserialization. Found by reading the code itself.",
      example: "SQL built by gluing strings together in a login handler",
    },
    {
      icon: <KeyRound className="h-4 w-4" />,
      title: "Leaked secrets",
      body: "Passwords and keys that were committed and forgotten, wherever they hide.",
      example: "a connection string with a password in appsettings.json",
    },
    {
      icon: <FileCode2 className="h-4 w-4" />,
      title: "Infrastructure files",
      body: "Dockerfiles, Kubernetes manifests, Terraform. Misconfigurations that undo application security.",
      example: "a container running as root in production",
    },
    {
      icon: <Scale className="h-4 w-4" />,
      title: "Licenses",
      body: "Every package's license, classified, before a copyleft obligation surprises a commercial codebase.",
      example: "a GPL dependency inside a proprietary product",
    },
    {
      icon: <Wrench className="h-4 w-4" />,
      title: "Code health",
      body: "Dead code, churn hotspots, abandoned migrations, runtime support status. The maintenance risk under the security risk.",
      example: "an app on a runtime Microsoft stopped patching",
      figure: { value: "Nov 2026", caption: "when .NET 8 stops receiving security patches. Paxley reports this per target framework." },
    },
  ];

  return (
    <Section id="product" container="none" padding="tight">
      <Panel tone="green">
        <div className="mx-auto max-w-6xl px-6 py-16 sm:px-10 md:py-20">
          <Reveal className="max-w-2xl">
            <Eyebrow className="!opacity-70">What it finds</Eyebrow>
            <Headline size="section" className="mt-4">
              One scan, <Em>five tools&rsquo; worth of answers.</Em>
            </Headline>
            <Subhead className="mt-4 !opacity-80">
              Each category below is a different analysis engine under the hood. Paxley runs
              them all and merges the results into one prioritized, deduplicated list.
            </Subhead>
          </Reveal>

          {/* 4-column bento: [2,1,1] / [1,1,2]. Six items, six cells, no gaps. */}
          <div className="mt-12 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {checks.map((c, i) => {
              const wide = Boolean(c.figure);
              return (
                <Reveal
                  key={c.title}
                  delay={0.05 * i}
                  className={`h-full ${wide ? "md:col-span-2" : ""}`}
                >
                  <div
                    className={`flex h-full flex-col rounded-2xl p-6 text-[#17150F] transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] hover:-translate-y-1 hover:shadow-[0_16px_40px_-20px_rgba(0,0,0,0.45)] ${
                      wide ? "bg-[#E4EFE4]" : "bg-[#FBF8F1]"
                    }`}
                  >
                    <div className="flex items-center gap-2.5 text-[#1E4D38]">
                      {c.icon}
                      <h3 className="text-base font-semibold text-[#17150F]">{c.title}</h3>
                    </div>
                    {wide && c.figure ? (
                      <div className="mt-5 grid gap-5 sm:grid-cols-[auto_1fr] sm:items-end">
                        <p className="text-5xl leading-none tracking-tight" style={SERIF}>
                          {c.figure.value}
                        </p>
                        <p className="text-sm leading-relaxed opacity-70 sm:pb-1">{c.figure.caption}</p>
                      </div>
                    ) : null}
                    <p className="mt-3 text-sm leading-relaxed opacity-75">{c.body}</p>
                    <p className="mt-auto pt-4 font-mono text-xs leading-relaxed opacity-50">
                      e.g. {c.example}
                    </p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </Panel>
    </Section>
  );
}

// ══════════════════════════════════════════════════════════════════
// 3. WHO IT'S FOR: two columns on a hairline, no boxes
// ══════════════════════════════════════════════════════════════════

function AudienceSection() {
  return (
    <Section>
      <Reveal className="max-w-2xl">
        <Headline size="section">
          Three very different jobs. <Em>Same evidence.</Em>
        </Headline>
      </Reveal>

      <div className="mt-12 grid gap-12 border-t border-[#E4DFD3] pt-10 lg:grid-cols-3 lg:gap-0">
        <Reveal className="lg:border-r lg:border-[#E4DFD3] lg:pr-10">
          <div className="flex items-center gap-3">
            <GitBranch className="h-5 w-5 text-[#1E4D38]" />
            <h3 className="text-xl font-semibold">Engineering teams</h3>
          </div>
          <p className="mt-4 text-base leading-relaxed opacity-75">
            Continuous scanning of the repositories you ship from. Findings arrive prioritized,
            known-exploited and reachable issues first, with fix guidance. The backlog becomes a
            work queue instead of a wall of alerts.
          </p>
          <ul className="mt-6 space-y-3 text-sm">
            {[
              "Baseline on the default branch; only new findings gate a change",
              "Suppress with an expiry date, so nothing is silenced forever",
              "Export anywhere: SARIF, CSV, JSON, HTML and more",
            ].map((f) => (
              <li key={f} className="flex items-start gap-2.5">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#1E4D38]" />
                <span className="opacity-85">{f}</span>
              </li>
            ))}
          </ul>
          <div className="mt-8">
            <GhostButton to="/onboarding" icon={<ArrowRight className="h-4 w-4" />}>
              Start free scan
            </GhostButton>
          </div>
        </Reveal>

        <Reveal delay={0.08} className="lg:border-r lg:border-[#E4DFD3] lg:px-10">
          <div className="flex items-center gap-3">
            <Building2 className="h-5 w-5 text-[#1E4D38]" />
            <h3 className="text-xl font-semibold">Investors &amp; acquirers</h3>
          </div>
          <p className="mt-4 text-base leading-relaxed opacity-75">
            A point-in-time code risk assessment for M&amp;A due diligence, vendor review, or
            underwriting. The target&rsquo;s source code stays inside a controlled environment.
            What leaves is the report: findings, dependency and license exposure, SBOM, and how
            much of the stack still receives security patches.
          </p>
          <ul className="mt-6 space-y-3 text-sm">
            {[
              "Fixed price per project: $1,500, no platform contract",
              "Report built for an investment committee, not a security team",
              "Continues into monthly monitoring only if you want it to",
            ].map((f) => (
              <li key={f} className="flex items-start gap-2.5">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#1E4D38]" />
                <span className="opacity-85">{f}</span>
              </li>
            ))}
          </ul>
          <div className="mt-8">
            <GhostButton to="/inquiry?interest=assessment" icon={<ArrowRight className="h-4 w-4" />}>
              Book an assessment
            </GhostButton>
          </div>
        </Reveal>

        <Reveal delay={0.16} className="lg:pl-10">
          <div className="flex items-center gap-3">
            <Landmark className="h-5 w-5 text-[#1E4D38]" />
            <h3 className="text-xl font-semibold">Regulated software makers</h3>
          </div>
          <p className="mt-4 text-base leading-relaxed opacity-75">
            Software sold into the EU now carries Cyber Resilience Act duties, and enterprise and
            public-sector buyers ask for SBOMs and secure development evidence. Paxley produces
            that evidence with every scan, so the answer is ready before the questionnaire arrives.
          </p>
          <ul className="mt-6 space-y-3 text-sm">
            {[
              "CycloneDX and SPDX SBOMs, plus OpenVEX export",
              "Vulnerability history with EPSS and CISA KEV context",
              "Evidence that supports CRA, NIST SSDF and EO 14028 reviews",
            ].map((f) => (
              <li key={f} className="flex items-start gap-2.5">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#1E4D38]" />
                <span className="opacity-85">{f}</span>
              </li>
            ))}
          </ul>
          <div className="mt-8">
            <GhostButton to="/inquiry?interest=trial" icon={<ArrowRight className="h-4 w-4" />}>
              Request a guided trial
            </GhostButton>
          </div>
        </Reveal>
      </div>
    </Section>
  );
}

// ══════════════════════════════════════════════════════════════════
// 4. HOW IT WORKS: the coal panel, split 5/7, steps as a numbered list
// ══════════════════════════════════════════════════════════════════

function HowItWorksSection() {
  const steps = [
    {
      title: "Point it at code",
      body: "Upload a ZIP, paste a Git URL, or connect GitHub. To keep source in your own infrastructure, run the GitHub Action on your runners or a self-hosted package.",
    },
    {
      title: "Every engine runs",
      body: "Static analysis, the full dependency graph including transitive packages, secrets, infrastructure files, containers, licenses, and code quality, in one pass.",
    },
    {
      title: "Read one report",
      body: "A single prioritized list with an AI-suggested fix and evidence for each finding, plus an SBOM in CycloneDX or SPDX. Most scans finish in minutes.",
    },
  ];
  const facts = [
    { k: "12 engines", v: "Opengrep, CodeQL, OSV-Scanner, Gitleaks, Trivy and more, in one pass" },
    { k: "Full graph", v: "direct and transitive dependencies resolved" },
    { k: "2 SBOM formats", v: "CycloneDX and SPDX, validator-clean" },
    { k: "8 exports", v: "SARIF, CSV, JSON, XML, HTML and more" },
  ];

  return (
    <Section id="how-it-works" container="none" padding="tight">
      <Panel tone="coal">
        <div className="mx-auto max-w-6xl px-6 py-16 sm:px-10 md:py-20">
          <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
            <Reveal className="lg:col-span-5">
              <Eyebrow className="!opacity-60">How it works</Eyebrow>
              <Headline size="section" className="mt-4">
                Code in. <Em>Report out.</Em>
              </Headline>
              <dl className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-1">
                {facts.map((f) => (
                  <div key={f.k} className="border-t border-white/15 pt-4">
                    <dt className="text-2xl" style={SERIF}>
                      {f.k}
                    </dt>
                    <dd className="mt-1 text-sm leading-relaxed opacity-60">{f.v}</dd>
                  </div>
                ))}
              </dl>
            </Reveal>

            <ol className="lg:col-span-7">
              {steps.map((s, i) => (
                <Reveal key={s.title} delay={0.07 * i}>
                  <li className="grid grid-cols-[3rem_1fr] gap-4 border-t border-white/15 py-7 first:border-t-0 first:pt-0 lg:first:pt-2">
                    <span className="text-4xl leading-none opacity-40" style={SERIF}>
                      {i + 1}
                    </span>
                    <div>
                      <h3 className="text-lg font-semibold">{s.title}</h3>
                      <p className="mt-2 max-w-xl text-sm leading-relaxed opacity-70">{s.body}</p>
                    </div>
                  </li>
                </Reveal>
              ))}
            </ol>
          </div>
        </div>
      </Panel>
    </Section>
  );
}

// ══════════════════════════════════════════════════════════════════
// 5. PRICING STRIP
// ══════════════════════════════════════════════════════════════════

function PricingStripSection() {
  const plans: Array<{
    name: string;
    price: string;
    cadence: string;
    best: string;
    features: string[];
    cta: { label: string; to?: string; href?: string };
    highlight?: boolean;
  }> = [
    {
      name: "Community",
      price: "$0",
      cadence: "forever",
      best: "Open source and solo developers",
      features: ["3 repositories", "5 scans a month", "SAST, SCA and secrets", "PR guard with comments"],
      cta: { label: "Start free scan", to: "/onboarding" },
    },
    {
      name: "Starter",
      price: "$199",
      cadence: "/month",
      best: "Small teams, up to 5 products",
      features: ["Up to 5 products or main repos", "150 MB source processing", "Full scanning stack", "SBOM and license governance"],
      cta: { label: "See pricing", to: "/pricing" },
    },
    {
      name: "Growth",
      price: "$499",
      cadence: "/month",
      best: "Medium teams, 5 to 10 products",
      features: ["5 to 10 products or main repos", "500 MB source processing", "Governance and assurance reporting", "Priority support"],
      cta: { label: "See pricing", to: "/pricing" },
      highlight: true,
    },
    {
      name: "Enterprise",
      price: "$999",
      cadence: "/month",
      best: "Unlimited self-hosted use",
      features: ["Unlimited products and repos", "Unlimited source processing", "M&A and diligence evidence outputs", "Dedicated support"],
      cta: { label: "Request a quote", to: "/inquiry?interest=enterprise" },
    },
  ];

  return (
    <Section id="pricing">
      <Reveal className="mx-auto max-w-3xl text-center">
        <Eyebrow>Pricing</Eyebrow>
        <Headline size="section" className="mt-4">
          Priced by the code you protect. <Em>Never per seat.</Em>
        </Headline>
        <Subhead className="mx-auto mt-4 max-w-xl">
          From $199 a month for up to 5 repositories, about $40 each. Fifty developers on ten
          repositories pay $499 on Growth, up to 90% less than a per-seat tool stack.
        </Subhead>
      </Reveal>

      <div className="mt-12 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {plans.map((p, i) => (
          <Reveal key={p.name} delay={0.06 * i} className="h-full">
            <div
              className={`flex h-full flex-col rounded-2xl border bg-white p-6 transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] hover:-translate-y-1 hover:shadow-[0_16px_44px_-22px_rgba(30,77,56,0.3)] ${
                p.highlight ? "border-[#1E4D38] shadow-[0_10px_36px_-16px_rgba(30,77,56,0.4)]" : "border-[#E4DFD3]"
              }`}
            >
              <h3 className="text-[11px] font-semibold uppercase tracking-[0.18em] opacity-50">
                {p.name}
              </h3>
              <div className="mt-3 flex items-baseline gap-1.5">
                <span className="text-3xl" style={SERIF}>
                  {p.price}
                </span>
                <span className="text-sm opacity-50">{p.cadence}</span>
              </div>
              <p className="mt-1 text-sm opacity-60">{p.best}</p>
              <ul className="mt-6 space-y-2 text-sm">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#1E4D38]" />
                    <span className="opacity-85">{f}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-auto pt-7">
                {p.cta.to ? (
                  p.highlight ? (
                    <GradientButton to={p.cta.to} className="w-full justify-center">
                      {p.cta.label}
                    </GradientButton>
                  ) : (
                    <GhostButton to={p.cta.to} className="w-full justify-center">
                      {p.cta.label}
                    </GhostButton>
                  )
                ) : (
                  <GhostButton href={p.cta.href} className="w-full justify-center">
                    {p.cta.label}
                  </GhostButton>
                )}
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </Section>
  );
}

// ══════════════════════════════════════════════════════════════════
// 6. FINAL CTA: green panel
// ══════════════════════════════════════════════════════════════════

function FinalCTASection() {
  return (
    <Section container="none" padding="tight" className="pb-20 md:pb-28">
      <Panel tone="green">
        <div className="mx-auto max-w-3xl px-6 py-16 text-center sm:px-10 md:py-20">
          <Reveal>
            <Headline size="section">
              See what&rsquo;s in <Em>your code.</Em>
            </Headline>
            <Subhead className="mx-auto mt-4 max-w-xl !opacity-80">
              Upload a ZIP or paste a Git URL, free and with no card. Prefer a guided start? Request
              a 30-day trial with our team, or book an assessment for an acquisition target.
            </Subhead>
            <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <span className="inline-flex">
                <GradientButton
                  to="/onboarding"
                  size="lg"
                  className="!border-[#F5F1E6]/30 !bg-[#F5F1E6] !text-[#17150F] hover:!bg-white"
                >
                  Start free scan
                </GradientButton>
              </span>
              <span className="inline-flex">
                <GhostButton
                  to="/inquiry?interest=trial"
                  size="lg"
                  className="!border-[#F5F1E6]/40 !bg-transparent !text-[#F5F1E6] hover:!bg-white/10"
                >
                  Request a guided trial
                </GhostButton>
              </span>
            </div>
          </Reveal>
        </div>
      </Panel>
    </Section>
  );
}
